import { User as UserGQL } from 'gql/generated-types/custom-graphql'
import {
	PRACTICE_ROLE_ADMIN,
	PRACTICE_ROLE_HOSPITAL_MANAGER,
	PRACTICE_ROLE_REGIONAL_MANAGER,
	PRACTICE_ROLE_SCRATCH_ADMIN,
	PRACTICE_ROLE_STAFF
} from '../constants'

// This will extract the username from the email if the email contains
// scratchpay.care as this is currently the way to create usernames
export const getUsernameOrEmail = (email) => {
	if (email && email.includes('scratchpay.care')) {
		return email.slice(0, email.indexOf('@'))
	}
	return email
}

export const canOrderTerminals = (user) => ['3', '5', '8', '11'].includes(user?.consolidatorId)

const SCRATCH_ADMIN_EMAIL_DOMAIN = '@scratchpay.com'

/**
 * Determines whether a user has scratch-admin access. Defense-in-depth: honors
 * the `isScratchAdmin` flag only when the account's email ends with
 * `@scratchpay.com`, so a stale or mis-provisioned flag on a non-Scratchpay
 * account cannot unlock admin surfaces. Mirrors the backend
 * `scratch-admin.middleware`.
 */
export const isScratchAdminUser = (user) =>
	!!user?.isScratchAdmin && !!user?.email?.toLowerCase().endsWith(SCRATCH_ADMIN_EMAIL_DOMAIN)

export const isAdminUser = (user: User) => !!user?.isAdmin

export const getUserRoleByPractice = (user: UserGQL, practiceId: string) => {
	if (user.isScratchAdmin) {
		return PRACTICE_ROLE_SCRATCH_ADMIN
	}

	if (user.isAdmin) {
		return PRACTICE_ROLE_ADMIN
	}

	if (user.isRegionalManager) {
		return PRACTICE_ROLE_REGIONAL_MANAGER
	}

	const userPractice = user.practices?.find(
		(x) => x.practice.id === practiceId?.replace('acct_', 'prac_')
	)

	if (userPractice) {
		if (userPractice.isRegionalManager) {
			return PRACTICE_ROLE_REGIONAL_MANAGER
		}

		if (userPractice.isHospitalManager) {
			return PRACTICE_ROLE_HOSPITAL_MANAGER
		}

		if (userPractice.isStaff) {
			return PRACTICE_ROLE_STAFF
		}
	}

	return ''
}

/**
 * Generic role-allowlist gate. Admin and scratch-admin always pass. Otherwise
 * the user must hold at least one role in `allowedRoles` on the current
 * practice (`userRolePractice`). Used by feature gates that follow the
 * "admin bypass + role allowlist" pattern (e.g. Stripe Capital surfaces,
 * `canViewUsers`).
 */
export const userHasRoleAccess = (
	user: User,
	userRolePractice: UserRolePractice | undefined,
	allowedRoles: ReadonlyArray<keyof UserRolePractice>
): boolean => {
	if (isAdminUser(user) || isScratchAdminUser(user)) return true
	if (!userRolePractice) return false
	return allowedRoles.some((role) => userRolePractice[role])
}

export const isStaff = (user: UserGQL, practiceId: string) => {
	const practiceUser = user.practices.find(
		(p) => p.practice.id === practiceId.replace('acct_', 'prac_')
	)
	return !practiceUser?.isRegionalManager && !practiceUser?.isHospitalManager
}
